"use client";

import React, { useEffect, useState } from "react";

import Image from "next/image";

import { FiX } from "react-icons/fi";

import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

import { useOtpLogin } from "@/hooks/useAuthMutations";

import Heading from "./Heading";

interface VerifyOTPProps {
  onNext: () => void;
  maxLength?: number;
  phoneNumber?: string;
  subTitle?: string;
  onCancel?: () => void;
  preventRedirectionOnSuccess?: boolean;
}

const VerifyOTP = ({
  onNext,
  maxLength = 4,
  phoneNumber,
  subTitle,
  preventRedirectionOnSuccess,
  onCancel,
}: VerifyOTPProps) => {
  const [otp, setOtp] = useState("");
  const otpLogin = useOtpLogin(preventRedirectionOnSuccess, onNext);
  const [errorCleared, setErrorCleared] = useState(false);
  const hasServerError = Boolean(otpLogin.isError);

  const handleSubmit = () => {
    if (otp.length !== maxLength || otpLogin.isPending) return;
    setErrorCleared(false);
    otpLogin.mutate({ phoneNumber: phoneNumber!, otp });
  };

  useEffect(() => {
    if (preventRedirectionOnSuccess && otpLogin.isSuccess) {
      onNext();
    }
  }, [otpLogin.isSuccess, preventRedirectionOnSuccess, onNext]);

  return (
    <div className="flex max-w-120 flex-col">
      <div className="mb-3.5 flex flex-row items-center justify-center">
        <Heading size="3xl" className="text-text-900 block flex-1">
          Verify OTP
        </Heading>
        {onCancel && (
          <button onClick={onCancel} className="rounded-full bg-gray-100 p-2 active:opacity-70">
            <FiX size={20} className="text-gray-600" />
          </button>
        )}
      </div>
      {subTitle ? (
        <p className="text-body-medium/md text-text-700 mb-10.5">{subTitle}</p>
      ) : (
        <p className="text-body-medium/md text-text-700 mb-10.5">
          Please enter the code sent to mobile number +91 {phoneNumber?.slice(0, 2)}******
          {phoneNumber?.slice(-2)}
        </p>
      )}

      <div className="mb-10.5">
        <InputOTP
          maxLength={maxLength}
          value={otp}
          onChange={(value) => {
            setOtp(value);
            if (hasServerError) setErrorCleared(true); // clear error styles while editing
          }}
          className="">
          <InputOTPGroup className="flex w-full items-center gap-6">
            {Array.from({ length: maxLength }).map((_, index) => (
              <InputOTPSlot
                key={index}
                index={index}
                className={` ${
                  hasServerError && !errorCleared ? "!border-error-500 !text-error-500" : ""
                }`}
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
        {hasServerError && !errorCleared && (
          <div className="mt-1 flex items-center">
            <Image
              className="p-0.25"
              src={"/images/invalid.svg"}
              height={14}
              width={14}
              alt="error"
            />
            <p className="text-error-500 text-body-normal/sm ml-1">Invalid OTP</p>
          </div>
        )}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={otp?.length !== maxLength || otpLogin.isPending}
        className="mb-4">
        {"Submit"}
      </Button>
    </div>
  );
};

export default VerifyOTP;
